import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  TextField,
  Grid,
  Chip,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  IconButton,
  InputAdornment,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Collapse,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  MyLocation as MyLocationIcon,
  Search as SearchIcon,
  DirectionsCar as CarIcon,
  LocalTaxi as TaxiIcon,
  TwoWheeler as BikeIcon,
  AirlineStops as VanIcon,
  Home as HomeIcon,
  Work as WorkIcon,
  Star as StarIcon,
  Add as AddIcon,
  LocationOn as LocationIcon,
} from '@mui/icons-material';
import MapComponent from '../components/MapComponent';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

const RequestRidePage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [language, setLanguage] = useState(i18n.language);

  const [pickup, setPickup] = useState({
    lat: 24.7136,
    lng: 46.6753,
    address: '',
    shortName: '',
  });
  const [dropoff, setDropoff] = useState({
    lat: 24.7136,
    lng: 46.6753,
    address: '',
    shortName: '',
  });
  const [vehicleType, setVehicleType] = useState('standard');
  const [estimatedFare, setEstimatedFare] = useState(null);
  const [distance, setDistance] = useState(null);
  const [duration, setDuration] = useState(null);

  const [savedAddresses, setSavedAddresses] = useState([]);
  const [showAddressPicker, setShowAddressPicker] = useState(false);
  const [addressType, setAddressType] = useState('pickup');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  useEffect(() => {
    if (user) {
      fetchSavedAddresses();
    }
  }, [user]);

  const fetchSavedAddresses = async () => {
    try {
      const response = await api.get(`/user/addresses?userId=${user.id}`);
      setSavedAddresses(response.data);
    } catch (err) {
      console.log('⚠️ لا يمكن تحميل العناوين المفضلة');
    }
  };

  const saveAddress = async (addressData) => {
    try {
      await api.post('/user/addresses', {
        ...addressData,
        userId: user.id,
      });
      fetchSavedAddresses();
    } catch (err) {
      console.log('⚠️ لا يمكن حفظ العنوان');
    }
  };

  const searchAddress = async (query) => {
    if (!query || query.length < 3) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=5&addressdetails=1&lang=${language}`
      );
      const data = await response.json();
      setSearchResults(data);
    } catch (err) {
      console.log('⚠️ خطأ في البحث عن العنوان');
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const selectSearchResult = (result, type) => {
    const lat = parseFloat(result.lat);
    const lng = parseFloat(result.lon);
    const address = result.display_name || result.name || '';

    if (type === 'pickup') {
      setPickup({ ...pickup, lat, lng, address });
    } else {
      setDropoff({ ...dropoff, lat, lng, address });
    }
    setSearchQuery('');
    setSearchResults([]);
    setShowAddressPicker(false);
  };

  const selectSavedAddress = (address, type) => {
    if (type === 'pickup') {
      setPickup({
        ...pickup,
        lat: address.lat,
        lng: address.lng,
        address: address.address,
        shortName: address.short_name,
      });
    } else {
      setDropoff({
        ...dropoff,
        lat: address.lat,
        lng: address.lng,
        address: address.address,
        shortName: address.short_name,
      });
    }
    setShowAddressPicker(false);
  };

  const calculateEstimate = () => {
    try {
      const R = 6371;
      const dLat = (dropoff.lat - pickup.lat) * Math.PI / 180;
      const dLng = (dropoff.lng - pickup.lng) * Math.PI / 180;
      const lat1 = pickup.lat * Math.PI / 180;
      const lat2 = dropoff.lat * Math.PI / 180;

      const a =
        Math.sin(dLat/2) * Math.sin(dLat/2) +
        Math.cos(lat1) * Math.cos(lat2) *
        Math.sin(dLng/2) * Math.sin(dLng/2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
      const dist = R * c;

      setDistance(dist);

      const baseFare = 5;
      const perKmRate = 2.5;
      const perMinuteRate = 0.5;
      const estimatedDuration = dist * 2;
      const fare = baseFare + (dist * perKmRate) + (estimatedDuration * perMinuteRate);

      setEstimatedFare(Math.round(fare * 100) / 100);
      setDuration(Math.round(estimatedDuration));

    } catch (err) {
      setError('حدث خطأ في حساب السعر');
    }
  };

  const handleRequestRide = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await api.post('/rides/request', {
        customerId: user.id,
        pickupAddress: pickup.address || 'موقع غير محدد',
        pickupLat: pickup.lat,
        pickupLng: pickup.lng,
        dropoffAddress: dropoff.address || 'موقع غير محدد',
        dropoffLat: dropoff.lat,
        dropoffLng: dropoff.lng,
        estimated_fare: estimatedFare || 25,
        vehicleType: vehicleType,
      });

      setSuccess(`✅ تم طلب الرحلة بنجاح! (رقم الرحلة: ${response.data.rideId})`);
      setTimeout(() => navigate('/'), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'حدث خطأ في طلب الرحلة');
    } finally {
      setLoading(false);
    }
  };

  const handleLanguageChange = (event, newLang) => {
    if (newLang) {
      setLanguage(newLang);
      i18n.changeLanguage(newLang);
    }
  };

  const vehicleTypes = [
    { id: 'standard', label: 'قياسي', icon: <CarIcon />, price: '' },
    { id: 'luxury', label: 'فاخر', icon: <TaxiIcon />, price: '+50%' },
    { id: 'van', label: 'فان', icon: <VanIcon />, price: '+30%' },
    { id: 'bike', label: 'دراجة', icon: <BikeIcon />, price: '-20%' },
  ];

  const getAddressIcon = (shortName) => {
    if (!shortName) return <StarIcon />;
    const name = shortName.toLowerCase();
    if (name.includes('منزل') || name.includes('home')) return <HomeIcon />;
    if (name.includes('عمل') || name.includes('work')) return <WorkIcon />;
    return <StarIcon />;
  };

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Paper sx={{ p: 3, borderRadius: 4 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
          <Box display="flex" alignItems="center">
            <IconButton onClick={() => navigate(-1)} sx={{ mr: 2 }}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h5" fontWeight={600}>
              🚗 {t('requestRide') || 'طلب رحلة'}
            </Typography>
          </Box>

          <ToggleButtonGroup
            value={language}
            exclusive
            onChange={handleLanguageChange}
            size="small"
            sx={{
              '& .MuiToggleButton-root': {
                borderRadius: 50,
                px: 2,
                '&.Mui-selected': {
                  background: 'linear-gradient(135deg, #FF1493, #FF69B4)',
                  color: '#FFFFFF',
                },
              },
            }}
          >
            <ToggleButton value="ar">🇸🇦</ToggleButton>
            <ToggleButton value="en">🇬🇧</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <MapComponent
              height="500px"
              initialLat={pickup.lat}
              initialLng={pickup.lng}
              onLocationSelect={(loc) => {
                setPickup({ ...pickup, lat: loc.lat, lng: loc.lng });
              }}
              markers={[
                { lat: pickup.lat, lng: pickup.lng, title: '📍 الانطلاق', color: '#FF1493' },
                { lat: dropoff.lat, lng: dropoff.lng, title: '🏁 الوجهة', color: '#4A00E0' },
              ]}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <Card sx={{ borderRadius: 3, height: '100%' }}>
              <CardContent>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  📋 {t('rideDetails') || 'تفاصيل الرحلة'}
                </Typography>

                <Divider sx={{ my: 2 }} />

                <Box mb={2}>
                  <Typography variant="caption" color="text.secondary">
                    📍 {t('pickupLocation') || 'نقطة الانطلاق'}
                  </Typography>
                  <Box display="flex" gap={1} alignItems="center">
                    <TextField
                      fullWidth
                      size="small"
                      value={pickup.address}
                      onChange={(e) => setPickup({ ...pickup, address: e.target.value })}
                      placeholder={t('enterPickup') || 'أدخل عنوان الانطلاق'}
                      sx={{ mt: 0.5 }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <MyLocationIcon sx={{ color: '#FF1493', fontSize: 18 }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                    <IconButton
                      size="small"
                      onClick={() => {
                        setAddressType('pickup');
                        setShowAddressPicker(!showAddressPicker);
                      }}
                      sx={{ color: '#FF1493' }}
                    >
                      <AddIcon />
                    </IconButton>
                  </Box>
                  {pickup.shortName && (
                    <Chip
                      label={pickup.shortName}
                      size="small"
                      sx={{ mt: 0.5, background: '#FF1493', color: '#fff' }}
                    />
                  )}
                </Box>

                <Box mb={2}>
                  <Typography variant="caption" color="text.secondary">
                    🏁 {t('dropoffLocation') || 'الوجهة'}
                  </Typography>
                  <Box display="flex" gap={1} alignItems="center">
                    <TextField
                      fullWidth
                      size="small"
                      value={dropoff.address}
                      onChange={(e) => setDropoff({ ...dropoff, address: e.target.value })}
                      placeholder={t('enterDropoff') || 'أدخل عنوان الوجهة'}
                      sx={{ mt: 0.5 }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon sx={{ color: '#4A00E0', fontSize: 18 }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                    <IconButton
                      size="small"
                      onClick={() => {
                        setAddressType('dropoff');
                        setShowAddressPicker(!showAddressPicker);
                      }}
                      sx={{ color: '#4A00E0' }}
                    >
                      <AddIcon />
                    </IconButton>
                  </Box>
                  {dropoff.shortName && (
                    <Chip
                      label={dropoff.shortName}
                      size="small"
                      sx={{ mt: 0.5, background: '#4A00E0', color: '#fff' }}
                    />
                  )}
                </Box>

                <Collapse in={showAddressPicker}>
                  <Paper sx={{ p: 2, mb: 2, borderRadius: 2, bgcolor: '#f8f9fa' }}>
                    <Typography variant="subtitle2" fontWeight={600}>
                      {t('savedAddresses') || 'العناوين المفضلة'}
                    </Typography>

                    <TextField
                      fullWidth
                      size="small"
                      placeholder={t('searchAddress') || 'ابحث عن عنوان...'}
                      value={searchQuery}
                      onChange={(e) => {
                        setSearchQuery(e.target.value);
                        searchAddress(e.target.value);
                      }}
                      sx={{ mt: 1 }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" />
                          </InputAdornment>
                        ),
                        endAdornment: isSearching && (
                          <InputAdornment position="end">
                            <CircularProgress size={20} />
                          </InputAdornment>
                        ),
                      }}
                    />

                    {searchResults.length > 0 && (
                      <List dense sx={{ mt: 1, maxHeight: 200, overflow: 'auto' }}>
                        {searchResults.map((result, index) => (
                          <ListItem
                            key={index}
                            button
                            onClick={() => selectSearchResult(result, addressType)}
                            sx={{ borderRadius: 1 }}
                          >
                            <ListItemIcon>
                              <LocationIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText
                              primary={result.name || result.display_name}
                              secondary={result.display_name}
                              primaryTypographyProps={{ variant: 'body2', noWrap: true }}
                              secondaryTypographyProps={{ variant: 'caption', noWrap: true }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    )}

                    {savedAddresses.length > 0 && (
                      <List dense sx={{ mt: 1 }}>
                        {savedAddresses.map((addr) => (
                          <ListItem
                            key={addr.id}
                            button
                            onClick={() => selectSavedAddress(addr, addressType)}
                            sx={{ borderRadius: 1 }}
                          >
                            <ListItemIcon>
                              {getAddressIcon(addr.short_name)}
                            </ListItemIcon>
                            <ListItemText
                              primary={addr.short_name}
                              secondary={addr.address}
                              primaryTypographyProps={{ variant: 'body2' }}
                              secondaryTypographyProps={{ variant: 'caption' }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    )}

                    <Button
                      size="small"
                      variant="outlined"
                      fullWidth
                      sx={{ mt: 1, borderRadius: 50 }}
                      onClick={() => {
                        const currentAddress = addressType === 'pickup' ? pickup : dropoff;
                        if (currentAddress.address) {
                          const shortName = prompt(t('enterShortName') || 'أدخل اسم مختصر للعنوان (مثل: المنزل، العمل)');
                          if (shortName) {
                            saveAddress({
                              address: currentAddress.address,
                              short_name: shortName,
                              lat: currentAddress.lat,
                              lng: currentAddress.lng,
                            });
                          }
                        } else {
                          alert(t('selectAddressFirst') || 'يرجى اختيار عنوان أولاً');
                        }
                      }}
                    >
                      + {t('saveCurrentAddress') || 'حفظ العنوان الحالي'}
                    </Button>
                  </Paper>
                </Collapse>

                <Button
                  fullWidth
                  variant="outlined"
                  onClick={calculateEstimate}
                  sx={{ mb: 2, borderRadius: 50 }}
                >
                  🧮 {t('calculateFare') || 'حساب السعر التقديري'}
                </Button>

                {estimatedFare && (
                  <Alert severity="info" sx={{ mb: 2, borderRadius: 3 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Box>
                        <Typography variant="body2" fontWeight={600}>
                          💰 {t('estimatedFare') || 'السعر التقديري'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {t('distance') || 'المسافة'}: {distance?.toFixed(1)} كم • {t('duration') || 'المدة'}: {duration} {t('minutes') || 'دقيقة'}
                        </Typography>
                      </Box>
                      <Typography variant="h6" fontWeight={700} sx={{ color: '#FF1493' }}>
                        {estimatedFare} {t('riyal') || 'ريال'}
                      </Typography>
                    </Box>
                  </Alert>
                )}

                <Typography variant="body2" fontWeight={600} gutterBottom>
                  🚘 {t('vehicleType') || 'نوع المركبة'}
                </Typography>
                <Grid container spacing={1} sx={{ mb: 2 }}>
                  {vehicleTypes.map((type) => (
                    <Grid item xs={6} key={type.id}>
                      <Button
                        fullWidth
                        variant={vehicleType === type.id ? 'contained' : 'outlined'}
                        size="small"
                        onClick={() => setVehicleType(type.id)}
                        sx={{
                          borderRadius: 3,
                          py: 1,
                          justifyContent: 'center',
                          gap: 1,
                          borderColor: vehicleType === type.id ? 'transparent' : 'rgba(255, 105, 180, 0.3)',
                        }}
                      >
                        {type.icon}
                        <Box textAlign="left">
                          <Typography variant="caption" display="block" fontWeight={600}>
                            {type.label}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" fontSize="10px">
                            {type.price}
                          </Typography>
                        </Box>
                      </Button>
                    </Grid>
                  ))}
                </Grid>

                <Button
                  fullWidth
                  variant="contained"
                  disabled={loading || !estimatedFare}
                  onClick={handleRequestRide}
                  sx={{ py: 1.5, borderRadius: 50 }}
                >
                  {loading ? <CircularProgress size={24} color="inherit" /> : '🚀 ' + (t('requestRide') || 'طلب الرحلة')}
                </Button>

                {error && (
                  <Alert severity="error" sx={{ mt: 2, borderRadius: 3 }}>
                    {error}
                  </Alert>
                )}
                {success && (
                  <Alert severity="success" sx={{ mt: 2, borderRadius: 3 }}>
                    {success}
                  </Alert>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Paper>
    </Container>
  );
};

export default RequestRidePage;
